import api from '../../api';

export default {
  name: 'accountDropdown',
  component() {
    return {
      showDropdown: false,
      showRegisterForm: false,
      showLoginForm: false,
      showForgotPasswordForm: false,

      isRegistering: false,
      registrationError: '',
      registrationSuccess: false,

      // Registration form data
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordConfirm: '',
      acceptsMarketing: false,
      terms: false,

      // Login form data
      loginEmail: '',
      loginPassword: '',
      isLoggingIn: false,
      loginError: '',

      // Forgot password form data
      recoverEmail: '',
      isRecovering: false,
      recoverError: '',
      recoverSuccess: false,

      init() {
        this.$watch('showDropdown', (newValue) => {
          this.handleHeaderState(newValue ? 'visible' : 'hidden');

          if (newValue) {
            // Add click outside listener when dropdown opens
            this.$nextTick(() => {
              document.addEventListener('click', (event) => this.handleClickOutside(event));
            });
          } else {
            // Remove click outside listener when dropdown closes
            document.removeEventListener('click', (event) => this.handleClickOutside(event));
          }
        });
      },

      // Dropdown functionalities
      toggleDropdown() {
        this.showDropdown = !this.showDropdown;

        this.$nextTick(() => {
          this.handleHeaderState(this.showDropdown ? 'visible' : 'hidden');
        });
      },

      handleClickOutside(event) {
        // Check if click is inside the trigger button first
        if (this.$el && this.$el.contains(event.target)) {
          return;
        }

        // Check if click is inside the teleported dropdown
        if (this.$refs.dropdown && this.$refs.dropdown.contains(event.target)) {
          return;
        }

        // If click is outside both elements, close the dropdown
        this.showDropdown = false;
      },

      // Form toggling
      toggleRegisterForm() {
        this.showRegisterForm = !this.showRegisterForm;
        this.showLoginForm = false;
        this.showForgotPasswordForm = false;
      },

      toggleLoginForm() {
        this.showLoginForm = !this.showLoginForm;
        this.showRegisterForm = false;
        this.showForgotPasswordForm = false;
      },

      toggleForgotPasswordForm() {
        this.showForgotPasswordForm = !this.showForgotPasswordForm;
        this.showLoginForm = false;
        this.showRegisterForm = false;
      },

      // Form submissions
      async registerCustomer() {
        this.isRegistering = true;
        this.registrationError = '';

        // Validate terms agreement
        if (!this.terms) {
          this.registrationError = 'Please agree to the terms and conditions';
          this.isRegistering = false;
          return;
        }

        // Validate password confirmation
        if (this.password !== this.passwordConfirm) {
          this.registrationError = 'Passwords do not match';
          this.isRegistering = false;
          return;
        }

        try {
          const mutation = `
            mutation customerCreate($input: CustomerCreateInput!) {
              customerCreate(input: $input) {
                customer {
                  id
                  email
                  firstName
                  lastName
                }
                customerUserErrors {
                  field
                  message
                }
              }
            }
          `;

          const variables = {
            input: {
              email: this.email,
              password: this.password,
              firstName: this.firstName,
              lastName: this.lastName,
              acceptsMarketing: this.acceptsMarketing
            }
          };

          const response = await fetch('/api/2025-07/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
            },
            body: JSON.stringify({
              query: mutation,
              variables: variables
            })
          });

          const result = await response.json();
          const { data } = result;
          const { customerCreate } = data || {};
          const { customer, customerUserErrors } = customerCreate || {};

          if (customerUserErrors?.length > 0) {
            this.registrationError = customerUserErrors[0].message;
          } else if (customer) {
            this.registrationSuccess = true;
            // Auto-login the user with the registered credentials using loginCustomer
            setTimeout(() => {
              this.loginEmail = this.email;
              this.loginPassword = this.password;
              this.loginCustomer();
            }, 1000);
          } else {
            this.registrationError = 'Registration failed. Please try again.';
          }
        } catch (error) {
          console.error('Registration error:', error);
          this.registrationError = 'Network error. Please try again.';
        } finally {
          this.isRegistering = false;
        }
      },

      async loginCustomer() {
        this.isLoggingIn = true;
        this.loginError = '';

        try {
          const mutation = `
            mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
              customerAccessTokenCreate(input: $input) {
                customerAccessToken {
                  accessToken
                  expiresAt
                }
                customerUserErrors {
                  field
                  message
                }
              }
            }
          `;

          const variables = {
            input: {
              email: this.loginEmail,
              password: this.loginPassword
            }
          };

          const response = await fetch('/api/2025-07/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
            },
            body: JSON.stringify({
              query: mutation,
              variables: variables
            })
          });

          const result = await response.json();
          const { data } = result;
          const { customerAccessTokenCreate } = data || {};
          const { customerAccessToken, customerUserErrors } = customerAccessTokenCreate || {};

          if (customerUserErrors?.length > 0) {
            this.loginError = `The email or password you entered is incorrect. Please try again.`;
            this.isLoggingIn = false;
          } else if (customerAccessToken) {
            localStorage.setItem('accessToken', customerAccessToken.accessToken);

            // Successfully got access token, now submit login form programmatically
            this.submitLoginForm(this.loginEmail, this.loginPassword);
          } else {
            this.loginError = `The email or password you entered is incorrect. Please try again.`;
            this.isLoggingIn = false;
          }
        } catch (error) {
          console.error('Login error:', error);
          this.loginError = 'Network error. Please try again.';
          this.isLoggingIn = false;
        }
      },

      async recoverPassword() {
        this.isRecovering = true;
        this.recoverError = '';
        this.recoverSuccess = false;

        try {
          const mutation = `
            mutation customerRecover($email: String!) {
              customerRecover(email: $email) {
                customerUserErrors {
                  field
                  message
                }
              }
            }
          `;

          const variables = {
            email: this.recoverEmail
          };

          const response = await fetch('/api/2025-07/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
            },
            body: JSON.stringify({
              query: mutation,
              variables: variables
            })
          });

          const result = await response.json();
          const { data } = result;
          const { customerRecover } = data || {};
          const { customerUserErrors } = customerRecover || {};

          if (customerUserErrors?.length > 0) {
            this.recoverError = customerUserErrors[0].message;
          } else {
            this.recoverSuccess = true;
            // Clear the form
            this.recoverEmail = '';
          }
        } catch (error) {
          console.error('Password recovery error:', error);
          this.recoverError = 'Network error. Please try again.';
        } finally {
          this.isRecovering = false;
        }
      },

      submitLoginForm(email, password) {
        // Create a form element programmatically
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/account/login';

        // Add form type
        const formTypeInput = document.createElement('input');
        formTypeInput.type = 'hidden';
        formTypeInput.name = 'form_type';
        formTypeInput.value = 'customer_login';
        form.appendChild(formTypeInput);

        // Add UTF-8
        const utfInput = document.createElement('input');
        utfInput.type = 'hidden';
        utfInput.name = 'utf8';
        utfInput.value = '✓';
        form.appendChild(utfInput);

        // Add email
        const emailInput = document.createElement('input');
        emailInput.type = 'hidden';
        emailInput.name = 'customer[email]';
        emailInput.value = email;
        form.appendChild(emailInput);

        // Add password
        const passwordInput = document.createElement('input');
        passwordInput.type = 'hidden';
        passwordInput.name = 'customer[password]';
        passwordInput.value = password;
        form.appendChild(passwordInput);

        // Add to document and submit
        document.body.appendChild(form);
        form.submit();
      },

      // Helpers
      handleHeaderState(state) {
        window.dispatchEvent(
          new CustomEvent('block-builder-state-change', {
            detail: {
              state: state,
              reparentSelector: '.header-block-builder'
            }
          })
        );
      }
    };
  }
};
